import { MarkdownRenderer } from '@xhubio-saas/core-frontend/kernel'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
} from '@xhubio-saas/core-frontend/ui-components'
import { formatDate, useLocale } from '@xhubio-saas/country-specifics-frontend'
import { useTranslation } from 'react-i18next'
import { useLegalDocument } from '../../api/legalClient'

/**
 * Public terms of service page
 *
 * Loads content from the database based on the current language.
 * Falls back to English if the current language is not available.
 */
export function TermsPage() {
  const { t } = useTranslation()
  const locale = useLocale()
  const { data: document, isLoading, error } = useLegalDocument('terms')

  if (isLoading) {
    return (
      <Card className="w-full">
        <CardHeader>
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-32 mt-2" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
        </CardContent>
      </Card>
    )
  }

  if (error || !document) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle>{t('legal.termsTitle', 'Terms of Service')}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">
            {t('legal.loadError', 'Failed to load document. Please try again later.')}
          </p>
        </CardContent>
      </Card>
    )
  }

  // Format the published date
  const publishedDate = document.publishedAt
    ? formatDate(document.publishedAt, { locale, withTime: false })
    : null

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>{document.title}</CardTitle>
        {publishedDate && (
          <p className="text-sm text-muted-foreground">
            {t('legal.lastUpdated', 'Last updated')}: {publishedDate}
          </p>
        )}
      </CardHeader>
      <CardContent>
        {document.content ? (
          <MarkdownRenderer content={document.content} />
        ) : (
          <p className="text-muted-foreground">{t('legal.noContent', 'No content available.')}</p>
        )}
      </CardContent>
    </Card>
  )
}
